import { useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Button } from '@/components/ui/button.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useApprovalStore } from '@/stores/approvalStore.js';

interface ApprovalCardProps {
    approvalId: string;
    toolName: string;
    toolArgs: Record<string, unknown>;
    message?: string;
    approvalStatus?: 'pending' | 'approved' | 'rejected'; // Status from message (for hydration)
}

export function ApprovalCard({
    approvalId,
    toolName,
    toolArgs,
    message,
    approvalStatus: propStatus,
}: ApprovalCardProps) {
    const { streamingService } = useServices();
    const [isProcessing, setIsProcessing] = useState(false);
    const [expanded, setExpanded] = useState(false);

    // Get approval status from store first, fallback to prop (for hydration after reload)
    const approval = useApprovalStore((state) =>
        Array.from(state.approvals.values()).find((a) => a.approvalId === approvalId)
    );
    // Store status takes priority (real-time updates), prop status is fallback (after reload)
    const status = approval?.status || propStatus || 'pending';
    const isPending = status === 'pending';
    const isApproved = status === 'approved';
    const isRejected = status === 'rejected';

    const handleApprove = async () => {
        try {
            setIsProcessing(true);
            streamingService.approveRequest(approvalId);
        } catch (error) {
            console.error('Failed to approve request:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async () => {
        try {
            setIsProcessing(true);
            streamingService.rejectRequest(approvalId);
        } catch (error) {
            console.error('Failed to reject request:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate user-friendly summary and extract source
    const getDisplayInfo = () => {
        let displayName = toolName;
        let source = '';

        // Extract MCP or internal prefix
        if (toolName.startsWith('mcp__')) {
            displayName = toolName.substring(5);
            source = 'mcp';
        } else if (toolName.startsWith('internal__')) {
            displayName = toolName.substring(10);
            source = 'internal';
        }

        let summary = '';
        if (message) {
            summary = message;
        } else {
            // Fallback: humanize tool name
            const humanized = displayName
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .toLowerCase()
                .trim();
            summary = `Approval required for ${humanized}`;
        }

        return { summary, displayName, source };
    };

    const { summary, displayName, source } = getDisplayInfo();

    return (
        <div className="flex gap-2.5 animate-slide-up my-1">
            {/* Timeline vertical line */}
            <div className="flex flex-col items-center">

                {/* Status indicator */}
                <div className="flex-shrink-0">
                    {isPending && (
                        <div className="relative">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                            {/* Pulse effect for pending */}
                            <span className="absolute inset-0 h-3.5 w-3.5 rounded-full bg-amber-500/30 animate-ping" />
                        </div>
                    )}
                    {isApproved && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                    {isRejected && <XCircle className="h-3.5 w-3.5 text-red-500" />}
                </div>

                {/* Vertical line continues below */}
                <div className={cn(
                    "w-px flex-1",
                    isPending ? "bg-amber-500/30" : isApproved ? "bg-green-500/20" : "bg-red-500/20"
                )} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pb-1">
                {/* Summary line */}
                <div className="space-y-1.5">
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="w-full flex items-center gap-1.5 text-left group"
                    >
                        <span className={cn(
                            "text-xs font-medium",
                            isPending ? "text-amber-600 dark:text-amber-400" :
                            isApproved ? "text-green-600 dark:text-green-400" :
                            "text-red-600 dark:text-red-400"
                        )}>
                            {summary}
                        </span>

                        {source && (
                            <span className="text-[10px] text-muted-foreground/40">
                                [{source}]
                            </span>
                        )}

                        <ChevronRight className={cn(
                            "h-2.5 w-2.5 text-muted-foreground/40 transition-transform flex-shrink-0",
                            expanded && "rotate-90"
                        )} />

                        {isPending && (
                            <span className="text-[10px] text-amber-600/60 dark:text-amber-400/60">
                                needs approval
                            </span>
                        )}
                    </button>

                    {/* Tool name (subtle) */}
                    <div className="text-[10px] text-muted-foreground/35">
                        {displayName}
                    </div>

                    {/* Action buttons for pending (inline) */}
                    {isPending && !expanded && (
                        <div className="flex gap-1.5 mt-1.5">
                            <Button
                                onClick={handleApprove}
                                disabled={isProcessing}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white h-6 text-[11px] px-2.5"
                            >
                                Approve
                            </Button>
                            <Button
                                onClick={handleReject}
                                disabled={isProcessing}
                                variant="outline"
                                size="sm"
                                className="h-6 text-[11px] px-2.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                            >
                                Reject
                            </Button>
                        </div>
                    )}
                </div>

                {/* Expanded details */}
                {expanded && (
                    <div className="mt-2 space-y-2 animate-fade-in">
                        {/* Tool arguments */}
                        {Object.keys(toolArgs).length > 0 && (
                            <div>
                                <h4 className="text-[9px] font-semibold text-muted-foreground/60 uppercase mb-1">
                                    Parameters
                                </h4>
                                <div className="bg-muted/30 rounded-md p-1.5 space-y-0.5">
                                    {Object.entries(toolArgs).map(([key, value]) => (
                                        <div key={key} className="flex gap-1.5 text-[10px]">
                                            <span className="text-muted-foreground font-medium shrink-0">
                                                {key}:
                                            </span>
                                            <span className="text-foreground/70 font-mono break-all">
                                                {typeof value === 'string'
                                                    ? value
                                                    : typeof value === 'object'
                                                        ? JSON.stringify(value)
                                                        : String(value)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action buttons for pending (expanded view) */}
                        {isPending && (
                            <div className="flex gap-1.5">
                                <Button
                                    onClick={handleApprove}
                                    disabled={isProcessing}
                                    size="sm"
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                                >
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Approve
                                </Button>
                                <Button
                                    onClick={handleReject}
                                    disabled={isProcessing}
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 h-7 text-xs"
                                >
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Reject
                                </Button>
                            </div>
                        )}

                        {/* Status message (for approved/rejected) */}
                        {!isPending && (
                            <div className="text-[10px] text-muted-foreground/60">
                                {isApproved && 'This request was approved'}
                                {isRejected && 'This request was rejected'}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
